import React from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useCollectionBinding,
  usePropertyField,
  usePropertyAddressUrl,
} from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import {
  FairHousingIcon,
  PetsAllowedIcon,
  WheelchairIcon,
  MultihubLogoMonoWhite,
  repliWhiteLogoUrl,
} from '@vibesites/assets';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebook, faInstagram } from '@fortawesome/free-brands-svg-icons';

// ============================================================================
// Helpers
// ============================================================================

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function toTelUrl(raw: string | null | undefined): string {
  if (!raw) return '';
  if (raw.startsWith('tel:')) return raw;
  const digits = raw.replace(/[^+\d]/g, '');
  return digits ? `tel:${digits}` : raw;
}

function toSmsUrl(raw: string | null | undefined): string {
  if (!raw) return '';
  if (raw.startsWith('sms:')) return raw;
  const digits = raw.replace(/[^+\d]/g, '');
  return digits ? `sms:${digits}` : raw;
}

// Split street onto its own line and keep "City, ST ZIP" together so the
// locality never wraps mid-phrase. On mobile the pin sits above this block;
// on desktop it sits beside it — either way two clean centered/left lines.
function renderAddress(raw: string): React.ReactNode {
  const parts = raw.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length < 3) return raw;
  const street = parts.slice(0, -2).join(', ');
  const locality = parts.slice(-2).join(', ');
  return (
    <>
      <span className="block">{street},</span>
      <span className="whitespace-nowrap">{locality}</span>
    </>
  );
}

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined' && normalized !== '0' && normalized !== 'closed';
}

// Canonicalize any of "9", "9:00", "09:00:00", "9 AM", "9:00am", "18:00"
// into a single display form ("9:00 am") so days that share the same hours
// always compare equal and merge into one range instead of repeating.
function formatTime(raw: string): string {
  const trimmed = raw.trim();
  const match = trimmed.match(/^(\d{1,2})(?::(\d{2}))?(?::\d{2})?\s*([ap]m)?$/i);
  if (!match) return trimmed;
  let hours = parseInt(match[1], 10);
  const minutes = match[2] ?? '00';
  const explicitPeriod = match[3]?.toLowerCase();
  const period = explicitPeriod ?? (hours >= 12 ? 'pm' : 'am');
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS: Record<string, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu',
  fri: 'Fri', sat: 'Sat', sun: 'Sun',
};

interface OfficeHour {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

interface GroupedHours {
  label: string;
  hours: string;
}

function normalizeDay(day: string): string {
  return day.trim().toLowerCase().slice(0, 3);
}

// Minutes since midnight, used to order a day's segments (e.g. morning
// before afternoon when a property has a midday/lunch break).
function timeToMinutes(raw: string): number {
  const m = formatTime(raw).match(/^(\d{1,2}):(\d{2}) (am|pm)$/);
  if (!m) return Number.MAX_SAFE_INTEGER;
  const h = parseInt(m[1], 10) % 12;
  return (m[3] === 'pm' ? h + 12 : h) * 60 + parseInt(m[2], 10);
}

function groupOfficeHours(raw: OfficeHour[]): GroupedHours[] {
  // 1. Collect every time segment for each day. A day with a midday break
  //    (lunch hours) arrives as multiple entries — keep all of them instead
  //    of collapsing the day to a single range.
  const byDay = new Map<string, { label: string; segments: { text: string; minutes: number }[] }>();

  for (const entry of raw) {
    if (!hasValue(entry.day)) continue;
    const key = normalizeDay(entry.day);
    if (!byDay.has(key)) {
      byDay.set(key, { label: DAY_LABELS[key] || entry.day, segments: [] });
    }
    if (hasValue(entry.open_time) && hasValue(entry.close_time)) {
      const text = `${formatTime(entry.open_time)} - ${formatTime(entry.close_time)}`;
      const day = byDay.get(key)!;
      // Skip an exact duplicate segment so it isn't listed twice.
      if (!day.segments.some((s) => s.text === text)) {
        day.segments.push({ text, minutes: timeToMinutes(entry.open_time) });
      }
    }
  }

  // 2. Build one combined hours string per day. Always include all seven
  //    weekdays so days the office is closed still show as "Closed", then
  //    append any custom entries (e.g. "Holidays") that aren't weekdays.
  //    Multiple segments join with a comma
  //    ("9:00 am - 12:00 pm, 1:00 pm - 6:00 pm").
  const customKeys = [...byDay.keys()].filter((k) => !DAY_ORDER.includes(k));
  const orderedKeys = [...DAY_ORDER, ...customKeys];

  const perDay = orderedKeys.map((key) => {
    const existing = byDay.get(key);
    const label = existing?.label ?? DAY_LABELS[key] ?? key;
    const segments = existing?.segments ?? [];
    const hours = segments.length === 0
      ? 'Closed'
      : segments
          .sort((a, b) => a.minutes - b.minutes)
          .map((s) => s.text)
          .join(', ');
    return { label, hours };
  });

  // 3. Merge consecutive days that share the identical full schedule into a
  //    single range. Days with a unique lunch pattern stay on their own row.
  const groups: GroupedHours[] = [];
  for (const { label, hours } of perDay) {
    const prev = groups[groups.length - 1];
    if (prev && prev.hours === hours) {
      const dash = prev.label.indexOf(' - ');
      const startLabel = dash !== -1 ? prev.label.slice(0, dash) : prev.label;
      // Guard against a range collapsing to the same day (e.g. "Mon - Mon").
      prev.label = startLabel === label ? startLabel : `${startLabel} - ${label}`;
    } else {
      groups.push({ label, hours });
    }
  }

  return groups;
}

// ============================================================================
// SVG Icons — matching the Header's stroke-style outline icons
// ============================================================================

function MapPinIcon({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg className={className} style={style} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function PhoneIcon({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg className={className} style={style} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  );
}

function SmartphoneIcon({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg className={className} style={style} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
    </svg>
  );
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const bgImageSlotDef: SlotGroupDefinition = {
  label: 'Footer Background Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const logoSlotDef: SlotGroupDefinition = {
  label: 'Footer Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_mono_white_logo', suggestedFields: ['property_mono_white_logo', 'property_primary_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const scheduleTourLinkDef: SlotGroupDefinition = {
  label: 'Schedule a Visit Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Visit' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const residentLoginLinkDef: SlotGroupDefinition = {
  label: 'Resident Login Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Login' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const applyNowLinkDef: SlotGroupDefinition = {
  label: 'Apply Now Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_apply_now_url' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const contactUsLinkDef: SlotGroupDefinition = {
  label: 'Contact Us Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/contact' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Contact Us' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const addressLinkDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    text: { label: 'Address Text', type: 'text', defaultField: 'property_full_address', defaultValue: '123 Main Street, City, ST 00000' },
    url: { label: 'URL', type: 'url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const phoneLinkDef: SlotGroupDefinition = {
  label: 'Phone Link',
  fields: {
    text: { label: 'Phone Text', type: 'text', defaultField: 'property_call_tracking_phone' },
    url: { label: 'URL', type: 'url', defaultField: 'property_call_tracking_phone' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const textUsLinkDef: SlotGroupDefinition = {
  label: 'Text Us Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Text Us!' },
    url: { label: 'URL', type: 'url', defaultField: 'property_call_tracking_phone' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const managedByLogoDef: SlotGroupDefinition = {
  label: 'Managed By Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_company_mono_white_logo', suggestedFields: ['property_company_mono_white_logo', 'property_company_logo', 'property_company_mono_black_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Property Management Company Logo' },
    url: { label: 'URL', type: 'url', defaultField: 'property_company_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const poweredByLogoDef: SlotGroupDefinition = {
  label: 'Powered By Logo',
  fields: {
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Multihub' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.multihub.io/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const designByLogoDef: SlotGroupDefinition = {
  label: 'Website Design By Logo',
  fields: {
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Repli' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.repli360.com/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const accessibilityLinkDef: SlotGroupDefinition = {
  label: 'Accessibility Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Accessibility Page' },
    url: { label: 'URL', type: 'url', defaultValue: '/accessibility' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const privacyPolicyLinkDef: SlotGroupDefinition = {
  label: 'Privacy Policy Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Privacy Policy' },
    url: { label: 'URL', type: 'url', defaultValue: '/privacy-policy' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const sitemapLinkDef: SlotGroupDefinition = {
  label: 'Sitemap Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Sitemap' },
    url: { label: 'URL', type: 'url', defaultValue: '/sitemap' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const fairHousingLinkDef: SlotGroupDefinition = {
  label: 'Fair Housing Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Fair Housing' },
    url: { label: 'URL', type: 'url', defaultValue: '/fair-housing' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const petPolicyLinkDef: SlotGroupDefinition = {
  label: 'Pet Policy Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Pet Friendly' },
    url: { label: 'URL', type: 'url', defaultValue: '/pet-policy' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const facebookLinkDef: SlotGroupDefinition = {
  label: 'Facebook',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_facebook_url' },
    label: { label: 'Label', type: 'text', defaultValue: 'Facebook' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const instagramLinkDef: SlotGroupDefinition = {
  label: 'Instagram',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_instagram_url' },
    label: { label: 'Label', type: 'text', defaultValue: 'Instagram' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ============================================================================
// Component
// ============================================================================

export function Footer() {
  const propertyName = useContentToken('footer.property_name');
  const callTrackingPhone = usePropertyField('property_call_tracking_phone') as string | null | undefined;
  const propertyPhone = usePropertyField('property_phone') as string | null | undefined;
  const propertyAddressUrl = usePropertyAddressUrl();

  // Slot groups
  const bgImage = useSlotGroup('footer.bg_image', bgImageSlotDef);
  const logo = useSlotGroup('footer.logo', logoSlotDef);
  const scheduleTourLink = useSlotGroup('footer.schedule_tour_link', scheduleTourLinkDef);
  const residentLoginLink = useSlotGroup('footer.resident_login_link', residentLoginLinkDef);
  const applyNowLink = useSlotGroup('footer.apply_now_link', applyNowLinkDef);
  const contactUsLink = useSlotGroup('footer.contact_us_link', contactUsLinkDef);
  const addressLink = useSlotGroup('footer.address', addressLinkDef);
  const phoneLink = useSlotGroup('footer.phone_link', phoneLinkDef);
  const textUsLink = useSlotGroup('footer.text_us_link', textUsLinkDef);
  const managedByLogo = useSlotGroup('footer.managed_by_logo', managedByLogoDef);
  const poweredByLogo = useSlotGroup('footer.powered_by_logo', poweredByLogoDef);
  const designByLogo = useSlotGroup('footer.design_by_logo', designByLogoDef);
  const accessibilityLink = useSlotGroup('footer.accessibility_link', accessibilityLinkDef);
  const privacyPolicyLink = useSlotGroup('footer.privacy_policy_link', privacyPolicyLinkDef);
  const sitemapLink = useSlotGroup('footer.sitemap_link', sitemapLinkDef);
  const fairHousingLink = useSlotGroup('footer.fair_housing_link', fairHousingLinkDef);
  const petPolicyLink = useSlotGroup('footer.pet_policy_link', petPolicyLinkDef);
  const facebookLink = useSlotGroup('footer.facebook_link', facebookLinkDef);
  const instagramLink = useSlotGroup('footer.instagram_link', instagramLinkDef);

  // Office hours
  const officeHours = useCollectionBinding<OfficeHour>('footer.office_hours', {
    collection: 'office_hours',
  });
  const groupedHours = officeHours && officeHours.length > 0
    ? groupOfficeHours(officeHours)
    : [];

  // Derived URLs
  const addressHref = addressLink.url || propertyAddressUrl || '#';
  const phoneHref = toTelUrl(phoneLink.url) || toTelUrl(callTrackingPhone) || toTelUrl(propertyPhone);
  const textUsHref = toSmsUrl(textUsLink.url) || toSmsUrl(callTrackingPhone) || toSmsUrl(propertyPhone);
  const phoneDisplay = formatPhone(phoneLink.text) || formatPhone(callTrackingPhone) || formatPhone(propertyPhone) || '(555) 555-5555';
  const bgSrc = bgImage.image || '';

  return (
    <footer>

      {/* ================================================================ */}
      {/* SECTION 1: Background Image with CTA Bar + Contact Info         */}
      {/* ================================================================ */}
      {/* `isolate` keeps the inner z-10 bars (CTA bar, banner, contact row) in
          their own stacking context so they don't out-stack fixed-position
          third-party widgets (e.g. chatbots) at the page root. */}
      <div className="footer-hero-section relative isolate text-white overflow-hidden">
        {/* Background image */}
        <EditableSlotGroup groupPath="footer.bg_image" definition={bgImageSlotDef}>
          <div className="absolute inset-0">
            {bgSrc ? (
              <VibesitesImage
                url={bgSrc}
                alt={bgImage.alt || ''}
                objectFit="cover"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gray-800" />
            )}
            {/* Dark overlay */}
            <div className="absolute inset-0 bg-black/60" />
          </div>
        </EditableSlotGroup>

        {/* CTA Links Bar — full width */}
        <div className="relative z-10 bg-black/80">
          <div className="grid grid-cols-2 md:grid-cols-4">
            <EditableSlotGroup groupPath="footer.schedule_tour_link" definition={scheduleTourLinkDef}>
              <a
                href={scheduleTourLink.url || '/schedule-a-tour'}
                target={scheduleTourLink._target || '_self'}
                className="footer-cta-link"
              >
                {scheduleTourLink.label || 'Schedule a Visit'}
              </a>
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="footer.resident_login_link" definition={residentLoginLinkDef}>
              <a
                href={residentLoginLink.url || '/resident-access'}
                target={residentLoginLink._target || '_self'}
                className="footer-cta-link"
              >
                {residentLoginLink.label || 'Resident Login'}
              </a>
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="footer.apply_now_link" definition={applyNowLinkDef}>
              <a
                href={applyNowLink.url || '/apply-now'}
                target={applyNowLink._target || '_self'}
                className="footer-cta-link"
              >
                {applyNowLink.label || 'Apply Now'}
              </a>
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="footer.contact_us_link" definition={contactUsLinkDef}>
              <a
                href={contactUsLink.url || '/contact'}
                target={contactUsLink._target || '_self'}
                className="footer-cta-link"
              >
                {contactUsLink.label || 'Contact Us'}
              </a>
            </EditableSlotGroup>
          </div>
        </div>

        {/* "Let's Talk" Banner */}
        <div className="relative z-10 px-4 sm:px-6 lg:px-8 pt-10 md:pt-14">
          <div className="max-w-5xl mx-auto">
            <div className="border border-white/60 py-4 md:py-5 px-6 text-center">
              <RichTextContent
                path="footer.lets_talk_heading"
                defaultValue="<p>LET'S TALK ABOUT YOUR NEW HOME</p>"
                as="p"
                className="font-heading text-base sm:text-lg md:text-xl lg:text-2xl tracking-[0.15em] uppercase text-white"
              />
            </div>
          </div>
        </div>

        {/* Contact Info Row — centered stack on mobile; 3-col from tablet (md+) */}
        <div className="relative z-10 px-4 sm:px-6 lg:px-8 py-10 md:py-14">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col items-center gap-8 md:grid md:grid-cols-3 md:gap-10 lg:gap-12 md:items-start">

              {/* Address — icon above centered text on mobile (matches phone/text rhythm);
                  icon beside text from tablet up. */}
              <EditableSlotGroup
                groupPath="footer.address"
                definition={addressLinkDef}
                className="flex justify-center md:justify-start"
              >
                <a
                  href={addressHref}
                  target={addressLink._target || '_blank'}
                  rel="noopener noreferrer"
                  className="inline-flex flex-col items-center gap-2 text-center md:flex-row md:items-start md:gap-2.5 lg:gap-3 md:text-left group"
                >
                  <span className="footer-icon-badge flex-shrink-0 md:mt-0.5">
                    <MapPinIcon className="w-5 h-5 md:w-4 md:h-4 lg:w-5 lg:h-5" />
                  </span>
                  <span className="font-heading text-lg md:text-base lg:text-xl text-white group-hover:text-white/80 transition-colors">
                    {renderAddress(addressLink.text || '123 Main Street, City, ST 00000')}
                  </span>
                </a>
              </EditableSlotGroup>

              {/* Phone + Text Us — centered in the middle column from tablet up */}
              <div className="flex flex-col items-center gap-5 md:gap-4 w-full">
                <EditableSlotGroup groupPath="footer.phone_link" definition={phoneLinkDef}>
                  <a
                    href={phoneHref || '#'}
                    className="inline-flex flex-col items-center gap-2 text-center md:flex-row md:items-center md:gap-2.5 lg:gap-3 group dniphone dniphonehref"
                  >
                    <span className="footer-icon-badge flex-shrink-0">
                      <PhoneIcon className="w-5 h-5 md:w-4 md:h-4 lg:w-5 lg:h-5" />
                    </span>
                    <span className="font-heading text-lg md:text-base lg:text-xl text-white group-hover:text-white/80 transition-colors">
                      {phoneDisplay}
                    </span>
                  </a>
                </EditableSlotGroup>
                <EditableSlotGroup groupPath="footer.text_us_link" definition={textUsLinkDef}>
                  <a
                    href={textUsHref || '#'}
                    className="inline-flex flex-col items-center gap-2 text-center md:flex-row md:items-center md:gap-2.5 lg:gap-3 group"
                  >
                    <span className="footer-icon-badge flex-shrink-0">
                      <SmartphoneIcon className="w-5 h-5 md:w-4 md:h-4 lg:w-5 lg:h-5" />
                    </span>
                    <span className="font-heading text-lg md:text-base lg:text-xl text-white group-hover:text-white/80 transition-colors">
                      {textUsLink.text || 'Text Us!'}
                    </span>
                  </a>
                </EditableSlotGroup>
              </div>

              {/* Office Hours */}
              <div className="text-center md:text-right w-full">
                <RichTextContent
                  path="footer.office_hours_label"
                  defaultValue="<p>Office Hours</p>"
                  as="h3"
                  className="font-heading text-lg md:text-base lg:text-xl text-white font-bold mb-3"
                />
                {groupedHours.length > 0 ? (
                  <dl className="footer-hours-list font-body text-sm md:text-[0.8125rem] lg:text-base text-white/90">
                    {groupedHours.map((group, index) => (
                      <React.Fragment key={index}>
                        <dt className="footer-hours-day">{group.label}</dt>
                        <dd className="footer-hours-time">
                          {group.hours.split(', ').map((segment, i) => (
                            <span key={i} className="footer-hours-segment">{segment}</span>
                          ))}
                        </dd>
                      </React.Fragment>
                    ))}
                  </dl>
                ) : (
                  <p className="font-body text-sm text-white/50">Office hours will be displayed here.</p>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* ================================================================ */}
      {/* SECTION 2: Dark Footer — Logo, Nav, Social                      */}
      {/* ================================================================ */}
      <div className="bg-[#1a1a1a] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-14">
          <div className="flex flex-col items-center gap-8">

            {/* Logo */}
            <EditableSlotGroup groupPath="footer.logo" definition={logoSlotDef}>
              <a href="/" className="block">
                {logo.image ? (
                  <VibesitesImage
                    url={logo.image}
                    alt={logo.alt || 'Community Logo'}
                    className="h-16 w-auto max-w-[220px] object-contain"
                  />
                ) : (
                  <div className="font-heading text-2xl text-white">Our Community</div>
                )}
              </a>
            </EditableSlotGroup>

            {/* Social Media */}
            <div className="flex items-center gap-4">
              <RichTextContent
                path="footer.social_label"
                defaultValue="<p>Social Media</p>"
                as="span"
                className="font-body text-sm text-gray-300"
              />
              <div className="flex items-center gap-3">
                <EditableSlotGroup groupPath="footer.facebook_link" definition={facebookLinkDef}>
                  {facebookLink.url ? (
                    <a
                      href={facebookLink.url}
                      target={facebookLink._target || '_blank'}
                      rel="noopener noreferrer"
                      aria-label={facebookLink.label || 'Facebook'}
                      className="text-gray-300 hover:text-white transition-colors"
                    >
                      <FontAwesomeIcon icon={faFacebook} className="text-xl" />
                    </a>
                  ) : (
                    <span className="text-gray-500">
                      <FontAwesomeIcon icon={faFacebook} className="text-xl" />
                    </span>
                  )}
                </EditableSlotGroup>
                <EditableSlotGroup groupPath="footer.instagram_link" definition={instagramLinkDef}>
                  {instagramLink.url ? (
                    <a
                      href={instagramLink.url}
                      target={instagramLink._target || '_blank'}
                      rel="noopener noreferrer"
                      aria-label={instagramLink.label || 'Instagram'}
                      className="text-gray-300 hover:text-white transition-colors"
                    >
                      <FontAwesomeIcon icon={faInstagram} className="text-xl" />
                    </a>
                  ) : (
                    <span className="text-gray-500">
                      <FontAwesomeIcon icon={faInstagram} className="text-xl" />
                    </span>
                  )}
                </EditableSlotGroup>
              </div>
            </div>

            {/* Navigation Links */}
            <nav className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 font-body text-sm text-gray-300" aria-label="Footer links">
              <EditableSlotGroup groupPath="footer.sitemap_link" definition={sitemapLinkDef}>
                <a
                  href={sitemapLink.url || '/sitemap'}
                  target={sitemapLink._target || '_self'}
                  className="hover:text-white transition-colors"
                >
                  {sitemapLink.text || 'Sitemap'}
                </a>
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="footer.accessibility_link" definition={accessibilityLinkDef}>
                <a
                  href={accessibilityLink.url || '/accessibility'}
                  target={accessibilityLink._target || '_self'}
                  className="hover:text-white transition-colors"
                >
                  {accessibilityLink.text || 'Accessibility Page'}
                </a>
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="footer.privacy_policy_link" definition={privacyPolicyLinkDef}>
                <a
                  href={privacyPolicyLink.url || '/privacy-policy'}
                  target={privacyPolicyLink._target || '_self'}
                  className="hover:text-white transition-colors"
                >
                  {privacyPolicyLink.text || 'Privacy Policy'}
                </a>
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="footer.fair_housing_link" definition={fairHousingLinkDef}>
                <a
                  href={fairHousingLink.url || '/fair-housing'}
                  target={fairHousingLink._target || '_self'}
                  className="hover:text-white transition-colors"
                >
                  {fairHousingLink.text || 'Fair Housing'}
                </a>
              </EditableSlotGroup>
            </nav>

          </div>
        </div>

        {/* ---- Divider ---- */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <hr className="border-t border-white/10" />
        </div>

        {/* ---- Bottom Attribution Bar ---- */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-center gap-5 text-gray-300 mb-4" aria-label="Compliance">
            <a
              href={fairHousingLink.url || '/fair-housing'}
              target={fairHousingLink._target || '_self'}
              className="hover:text-white transition-colors"
              aria-label={fairHousingLink.text || 'Fair Housing'}
            >
              <FairHousingIcon size={24} color="currentColor" aria-hidden="true" />
            </a>
            <EditableSlotGroup groupPath="footer.pet_policy_link" definition={petPolicyLinkDef}>
              <a
                href={petPolicyLink.url || '/pet-policy'}
                target={petPolicyLink._target || '_self'}
                className="hover:text-white transition-colors"
                aria-label={petPolicyLink.text || 'Pet Friendly'}
              >
                <PetsAllowedIcon size={20} color="currentColor" aria-hidden="true" />
              </a>
            </EditableSlotGroup>
            <a
              href={accessibilityLink.url || '/accessibility'}
              target={accessibilityLink._target || '_self'}
              className="hover:text-white transition-colors"
              aria-label={accessibilityLink.text || 'Accessibility'}
            >
              <WheelchairIcon size={20} color="currentColor" aria-hidden="true" />
            </a>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 items-center gap-4">
            <div className="flex items-center justify-center md:justify-start">
              <EditableSlotGroup groupPath="footer.managed_by_logo" definition={managedByLogoDef}>
                {managedByLogo.image ? (
                  <a href={managedByLogo.url || '#'} target={managedByLogo._target || '_blank'} rel="noopener noreferrer" className="opacity-90 hover:opacity-100 transition-opacity">
                    <VibesitesImage
                      url={managedByLogo.image}
                      alt={managedByLogo.alt || 'Property Management Company'}
                      className="h-8 w-auto max-w-[160px] object-contain"
                    />
                  </a>
                ) : null}
              </EditableSlotGroup>
            </div>
            <p className="font-body text-[11px] text-gray-400 text-center">
              &copy; {new Date().getFullYear()}{' '}
              <EditableToken
                path="footer.property_name"
                defaultField="property_name"
                defaultValue="Property Name"
              >
                {propertyName || 'Property Name'}
              </EditableToken>
              {' '}All Rights Reserved.
            </p>
            <div className="flex items-center justify-center md:justify-end gap-3">
              <EditableSlotGroup groupPath="footer.powered_by_logo" definition={poweredByLogoDef}>
                <a href={poweredByLogo.url || 'https://www.multihub.io/'} target={poweredByLogo._target || '_blank'} rel="noopener noreferrer" className="opacity-90 hover:opacity-100 transition-opacity">
                  <MultihubLogoMonoWhite height={20} alt={poweredByLogo.alt || 'Multihub'} />
                </a>
              </EditableSlotGroup>
              <span className="text-gray-400" aria-hidden="true">·</span>
              <EditableSlotGroup groupPath="footer.design_by_logo" definition={designByLogoDef}>
                <a href={designByLogo.url || 'https://www.repli360.com/'} target={designByLogo._target || '_blank'} rel="noopener noreferrer" className="opacity-90 hover:opacity-100 transition-opacity">
                  <VibesitesImage
                    url={repliWhiteLogoUrl}
                    alt={designByLogo.alt || 'Repli'}
                    height={28}
                    className="h-7 w-auto"
                    priority
                  />
                </a>
              </EditableSlotGroup>
            </div>
          </div>
        </div>
      </div>

      {/* ================================================================ */}
      {/* Scoped Styles                                                   */}
      {/* ================================================================ */}
      <style>{`
        .footer-cta-link {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0.875rem 0.5rem;
          font-family: var(--vibe-font-heading, var(--vibe-font-body, sans-serif));
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          white-space: nowrap;
          color: #fff;
          text-decoration: none;
          transition: background-color 0.25s ease, color 0.25s ease;
        }
        .footer-cta-link:hover {
          background-color: var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-secondary);
        }
        @media (min-width: 768px) {
          .footer-cta-link {
            font-size: 0.72rem;
            letter-spacing: 0.12em;
            padding: 1rem 0.5rem;
          }
        }
        @media (min-width: 1024px) {
          .footer-cta-link {
            font-size: 0.8rem;
            letter-spacing: 0.18em;
            padding: 1rem 1.25rem;
          }
        }
        .footer-icon-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 2rem;
          height: 2rem;
          flex-shrink: 0;
          color: #fff;
        }
        @media (min-width: 768px) and (max-width: 1023px) {
          .footer-icon-badge {
            width: 1.5rem;
            height: 1.5rem;
          }
        }
        .footer-hours-list {
          display: grid;
          grid-template-columns: max-content max-content;
          justify-content: center;
          align-items: start;
          column-gap: 1.5rem;
          row-gap: 0.25rem;
          margin: 0;
        }
        .footer-hours-day {
          margin: 0;
          text-align: left;
          white-space: nowrap;
        }
        .footer-hours-time {
          margin: 0;
          text-align: right;
          font-variant-numeric: tabular-nums;
        }
        .footer-hours-segment {
          display: block;
          white-space: nowrap;
        }
        @media (min-width: 768px) {
          .footer-hours-list {
            justify-content: end;
          }
        }
      `}</style>

    </footer>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Footer',
  displayName: 'Footer',
  tokens: {
    'footer.property_name': {
      label: 'Property Name',
      type: 'text',
      defaultField: 'property_name',
      defaultValue: 'Property Name',
    },
    'footer.lets_talk_heading': {
      label: 'Let\'s Talk Heading',
      type: 'richtext',
      defaultValue: '<p>LET\'S TALK ABOUT YOUR NEW HOME</p>',
    },
    'footer.office_hours_label': {
      label: 'Office Hours Label',
      type: 'richtext',
      defaultValue: '<p>Office Hours</p>',
    },
    'footer.social_label': {
      label: 'Social Media Label',
      type: 'richtext',
      defaultValue: '<p>Social Media</p>',
    },
  },
  slotGroups: {
    'footer.bg_image': bgImageSlotDef,
    'footer.logo': logoSlotDef,
    'footer.schedule_tour_link': scheduleTourLinkDef,
    'footer.resident_login_link': residentLoginLinkDef,
    'footer.apply_now_link': applyNowLinkDef,
    'footer.contact_us_link': contactUsLinkDef,
    'footer.address': addressLinkDef,
    'footer.phone_link': phoneLinkDef,
    'footer.text_us_link': textUsLinkDef,
    'footer.managed_by_logo': managedByLogoDef,
    'footer.powered_by_logo': poweredByLogoDef,
    'footer.design_by_logo': designByLogoDef,
    'footer.accessibility_link': accessibilityLinkDef,
    'footer.privacy_policy_link': privacyPolicyLinkDef,
    'footer.sitemap_link': sitemapLinkDef,
    'footer.fair_housing_link': fairHousingLinkDef,
    'footer.pet_policy_link': petPolicyLinkDef,
    'footer.facebook_link': facebookLinkDef,
    'footer.instagram_link': instagramLinkDef,
  },
  collectionBindings: {
    'footer.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Footer as any).contentSchema = contentSchema;

export default Footer;
